import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { updateProfile } from 'firebase/auth';
import { auth, db } from '../config/firebase';
import { useAuthStore } from '../store/authStore';
import { 
  User, 
  Lock, 
  Mail, 
  Globe,
  Upload,
  Check,
  Loader
} from 'lucide-react';
import { useMediaApi } from '../hooks/useMediaApi';
import { uploadProfileImage } from '../utils/profileImageUpload';
import { getFirestore, doc, updateDoc } from 'firebase/firestore';
import { logger } from '../utils/logger';
import { PageLayout } from '../components/layout';

// Placeholder service function (move to a service file later)
const updateUserProfile = async (userId: string, data: { firstName?: string; lastName?: string; name?: string }) => {
  if (!userId) throw new Error('User ID is required');
  const userDocRef = doc(db, 'users', userId);
  await updateDoc(userDocRef, {
    ...data,
    updatedAt: new Date().toISOString(),
  });
};

const Settings = () => {
  const { t, i18n } = useTranslation();
  const { user, setUser } = useAuthStore();
  const [firstName, setFirstName] = useState(user?.firstName || '');
  const [lastName, setLastName] = useState(user?.lastName || '');
  const [email, setEmail] = useState(user?.email || '');
  const [newEmail, setNewEmail] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [selectedLanguage, setSelectedLanguage] = useState(i18n.language.split('-')[0]);
  const [showEmailSuccess, setShowEmailSuccess] = useState(false);
  const [showPasswordSuccess, setShowPasswordSuccess] = useState(false);
  const [showProfileSuccess, setShowProfileSuccess] = useState(false);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(user?.photoURL || null);
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);
  const [profileUpdateError, setProfileUpdateError] = useState<string | null>(null);
  const [isUpdatingProfile, setIsUpdatingProfile] = useState(false);
  // Add a workaround state to force French if the UI is already in French but not being detected
  const [forceFrench] = useState(document.title.includes('Paramètres'));
  
  // Initialize the media API for image uploads
  const mediaApi = useMediaApi();

  // Initialize Firestore
  const db = getFirestore();

  // Ensure the selectedLanguage state is synchronized with i18n
  useEffect(() => {
    const normalizedLang = i18n.language.split('-')[0];
    setSelectedLanguage(normalizedLang);
  }, [i18n.language]);

  // Log on component mount
  useEffect(() => {
    const normalizedLang = i18n.language.split('-')[0];
    setSelectedLanguage(normalizedLang);
  }, []);

  // Additional effect to check if page title indicates we're in French mode
  useEffect(() => {
    if (document.title.includes('Paramètres')) {
      setSelectedLanguage('fr');
    }
    
    const titleObserver = new MutationObserver(() => {
      if (document.title.includes('Paramètres')) {
        setSelectedLanguage('fr');
      }
    });
    
    titleObserver.observe(document.querySelector('title') || document.head, {
      subtree: true,
      characterData: true,
      childList: true
    });
    
    return () => titleObserver.disconnect();
  }, []);

  const languages = [
    { code: 'en', name: 'English', flag: '🇬🇧' },
    { code: 'fr', name: 'Français', flag: '🇫🇷' },
    { code: 'es', name: 'Español', flag: '🇪🇸' }
  ];

  const handleLanguageChange = (languageCode: string) => {
    setSelectedLanguage(languageCode);
    i18n.changeLanguage(languageCode);
  };

  const handleProfileUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsUpdatingProfile(true);
    setProfileUpdateError(null);
    setShowProfileSuccess(false);

    const currentUser = auth.currentUser;
    if (!currentUser) {
      setProfileUpdateError(t('settings.profile.error.noAuth', 'Authentication error.'));
      setIsUpdatingProfile(false);
      return;
    }

    const newFullName = `${firstName} ${lastName}`.trim();
    const updateData = {
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      name: newFullName,
    };

    try {
      await updateProfile(currentUser, {
        displayName: newFullName
      });
      
      await updateUserProfile(currentUser.uid, updateData);

      setUser({
        ...user!,
        firstName: updateData.firstName,
        lastName: updateData.lastName,
        name: updateData.name, 
      });

      setShowProfileSuccess(true);
      setTimeout(() => setShowProfileSuccess(false), 3000);

    } catch (error: any) {
      logger.error('Settings', 'Error updating profile', error);
      setProfileUpdateError(error.message || t('settings.profile.error.generic', 'Failed to update profile.'));
    } finally {
      setIsUpdatingProfile(false);
    }
  };

  const handleEmailChange = (e: React.FormEvent) => {
    e.preventDefault();
    setEmail(newEmail);
    setNewEmail('');
    setShowEmailSuccess(true);
    setTimeout(() => setShowEmailSuccess(false), 3000);
  };

  const handlePasswordChange = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setShowPasswordSuccess(true);
    setTimeout(() => setShowPasswordSuccess(false), 3000);
  };

  const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      try {
        const previewUrl = URL.createObjectURL(file);
        setAvatarPreview(previewUrl);
        setIsUploadingAvatar(true);
        
        logger.info('Settings', `Uploading avatar: ${file.name}`);
        
        // Upload the file to storage
        const imageUrl = await uploadProfileImage(file);
        
        // Update Firebase profile with new photo URL
        const currentUser = auth.currentUser;
        if (currentUser) {
          // Update auth profile
          await updateProfile(currentUser, {
            photoURL: imageUrl
          });
          
          // Also update the user document in Firestore
          try {
            await updateDoc(doc(db, 'users', currentUser.uid), {
              photoURL: imageUrl,
              updatedAt: new Date().toISOString()
            });
            logger.info('Settings', 'User document updated with new photoURL in Firestore');
          } catch (firestoreError) {
            logger.error('Settings', 'Error updating user document in Firestore', firestoreError);
            // Continue even if Firestore update fails, as auth profile is updated
          }
          
          // Update the user in the auth store
          setUser({
            ...user!,
            photoURL: imageUrl
          });
          
          logger.info('Settings', `Avatar updated successfully with URL: ${imageUrl}`);
          
          // Show success message
          setShowProfileSuccess(true);
          setTimeout(() => setShowProfileSuccess(false), 3000);
        }
      } catch (error) {
        logger.error('Settings', 'Error uploading avatar', error);
        alert(t('settings.avatarUploadFailed', 'Failed to upload avatar image. Please try again.'));
        // Revert to previous avatar if there was one
        setAvatarPreview(user?.photoURL || null);
      } finally {
        setIsUploadingAvatar(false);
        
        // Clean up the object URL to prevent memory leaks
        if (avatarPreview && avatarPreview !== user?.photoURL) {
          URL.revokeObjectURL(avatarPreview);
        }
      }
    }
  };

  return (
    <PageLayout
      title={t('common.settings')}
      subtitle={t('settings.manageAccount', 'Manage your account settings and preferences')}
    >

          <div className="space-y-6">
            {/* Profile Card */}
            <form onSubmit={handleProfileUpdate} className="bg-white rounded-xl border border-black shadow-lg p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <User className="w-5 h-5 text-gray-400" />
                  <h2 className="text-lg font-semibold text-gray-900">{t('settings.profile', 'Profile')}</h2>
                </div>
                {showProfileSuccess && (
                  <div className="flex items-center gap-2 text-green-600">
                    <Check className="w-4 h-4" />
                    <span className="text-sm">{t('settings.profile.success', 'Profile updated successfully')}</span>
                  </div>
                )}
                {profileUpdateError && (
                  <div className="flex items-center gap-2 text-red-600">
                    <span className="text-sm">{profileUpdateError}</span>
                  </div>
                )}
              </div>

              {/* Combined Avatar and Name Input Row */}
              <div className="flex flex-col md:flex-row items-start gap-6 mb-6">
                {/* Avatar Section */}
                <div className="relative flex-shrink-0">
                  <div className="w-24 h-24 rounded-full bg-gray-100 flex items-center justify-center overflow-hidden">
                    {isUploadingAvatar ? (
                      <div className="flex items-center justify-center h-full w-full bg-gray-200 bg-opacity-50">
                        <Loader className="w-8 h-8 text-primary-600 animate-spin" />
                      </div>
                    ) : avatarPreview ? (
                      <img
                        src={avatarPreview}
                        alt={t('settings.profile', 'Profile preview')}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <User className="w-12 h-12 text-gray-400" />
                    )}
                  </div>
                  <label 
                    htmlFor="avatar-upload"
                    className="absolute bottom-0 right-0 p-1.5 bg-white rounded-full border border-gray-200 shadow-sm cursor-pointer hover:border-primary-500"
                  >
                    <Upload className="w-4 h-4 text-gray-500" />
                  </label>
                  <input
                    id="avatar-upload"
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleAvatarUpload}
                    disabled={isUploadingAvatar}
                  />
                </div>
                
                {/* First Name and Last Name Inputs Section */}
                <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-1">
                      {t('settings.profile.firstName', 'First Name')}
                    </label>
                    <input
                      type="text"
                      id="firstName"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-1">
                      {t('settings.profile.lastName', 'Last Name')}
                    </label>
                    <input
                      type="text"
                      id="lastName"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>
                  {/* Profile Picture Description - Moved under names */}
                  <div className="sm:col-span-2">
                     <p className="text-xs text-gray-500 mt-1">
                      {t('settings.uploadProfilePicture', 'Upload a new profile picture. The image should be at least 400x400 pixels.')}
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Language Selection */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('settings.language.title', 'Language Preference')}</label>
                <div className="grid grid-cols-3 gap-4">
                  {languages.map((language) => {
                    const isSelected = selectedLanguage === language.code || 
                                      (language.code === 'fr' && forceFrench);
                    
                    return (
                      <button
                        key={language.code}
                        onClick={() => handleLanguageChange(language.code)}
                        className={`flex items-center gap-3 p-4 rounded-lg ${
                          isSelected
                            ? 'border-2 border-primary-600 bg-[#CFBFF7] bg-opacity-30'
                            : 'border border-gray-200 hover:border-primary-600'
                        }`}
                      >
                        <span className="text-2xl">{language.flag}</span>
                        <div className="text-left">
                          <div className="font-medium text-gray-900">{language.name}</div>
                          <div className="text-sm text-gray-500">{language.code.toUpperCase()}</div>
                        </div>
                        {isSelected && (
                          <Check className="w-5 h-5 text-primary-600 ml-auto stroke-[2.5]" />
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="mt-6 flex justify-end">
                <button
                  type="submit"
                  disabled={isUpdatingProfile}
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  {isUpdatingProfile && <Loader className="animate-spin -ml-1 mr-2 h-4 w-4" />}
                  {t('settings.profile.updateButton', 'Save Changes')}
                </button>
              </div>
            </form>

            {/* Email and Security Cards Row */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Email Card */}
              <div className="bg-white rounded-xl border border-black shadow-lg p-6">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <Mail className="w-5 h-5 text-gray-400" />
                    <h2 className="text-lg font-semibold text-gray-900">{t('settings.email.title', 'Email Address')}</h2>
                  </div>
                  {showEmailSuccess && (
                    <div className="flex items-center gap-2 text-green-600">
                      <Check className="w-4 h-4" />
                      <span className="text-sm">{t('settings.email.success', 'Email updated successfully')}</span>
                    </div>
                  )}
                </div>

                <form onSubmit={handleEmailChange} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('settings.email.current', 'Current Email')}
                    </label>
                    <input
                      type="email"
                      value={email}
                      disabled
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('settings.email.new', 'New Email')}
                    </label>
                    <input
                      type="email"
                      value={newEmail}
                      onChange={(e) => setNewEmail(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      placeholder={t('settings.email.placeholder', 'Enter your new email')}
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={!newEmail}
                    className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {t('settings.email.update', 'Update Email')}
                  </button>
                </form>
              </div>

              {/* Security Card */}
              <div className="bg-white rounded-xl border border-black shadow-lg p-6">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <Lock className="w-5 h-5 text-gray-400" />
                    <h2 className="text-lg font-semibold text-gray-900">{t('settings.security.title', 'Security')}</h2>
                  </div>
                  {showPasswordSuccess && (
                    <div className="flex items-center gap-2 text-green-600">
                      <Check className="w-4 h-4" />
                      <span className="text-sm">{t('settings.security.success', 'Password updated successfully')}</span>
                    </div>
                  )}
                </div>

                <form onSubmit={handlePasswordChange} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('settings.security.currentPassword', 'Current Password')}
                    </label>
                    <input
                      type="password"
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      placeholder={t('settings.security.currentPasswordPlaceholder', 'Enter your current password')}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('settings.security.newPassword', 'New Password')}
                    </label>
                    <input
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      placeholder={t('settings.security.newPasswordPlaceholder', 'Enter your new password')}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('settings.security.confirmPassword', 'Confirm New Password')}
                    </label>
                    <input
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      placeholder={t('settings.security.confirmPasswordPlaceholder', 'Confirm your new password')}
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={!currentPassword || !newPassword || !confirmPassword || newPassword !== confirmPassword}
                    className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {t('settings.security.update', 'Update Password')}
                  </button>
                </form>
              </div>
            </div>
          </div>
    </PageLayout>
  );
};

export default Settings;